import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { getIconAtlas } from '../icon-atlas.js';
import { construireVivier, actives, tirer } from '../poster-icons.js';
import { cleanDisplayTitle, cleanDisplayArtist } from '../title-clean.js';
import { PosterBackdrop, BACKDROP_TIERS, ZONE_CHUNK, LUMA } from '../poster-backdrop.js';

// Poster - une enseigne cinétique. Des pictogrammes de NÉON sur du noir.
//
// CE VISUALISEUR A CHANGÉ DE LANGAGE. Il était fait d'aplats de couleur
// saturée : de l'encre, de l'impression, Saul Bass. Sur un téléviseur dans une
// pièce sombre, un aplat vermillon qui occupe la moitié du cadre pendant une
// mesure est agressif - une affiche est faite pour être vue à la lumière du
// jour, sur du papier qui REFLÈTE ; un écran ÉMET. Le registre est maintenant
// celui de la lumière : fond noir, une seule teinte à l'écran, et le contraste
// vient de ce qui brille sur ce noir, jamais de deux couleurs qui s'opposent.
//
// CE QUI A ÉTÉ GARDÉ, à l'identique : la sélection sémantique des icônes
// (poster-icons.js), les quatre motifs rythmiques, le punch d'apparition, le
// bandeau typographique, les anneaux d'écho, la grille du drop.
//
// LE FOND VIT DANS poster-backdrop.js depuis le 2026-09-26, extrait tel quel
// pour que Pyro le prenne aussi ; ce fichier garde les pictogrammes, les
// motifs, la grille du drop et l'horloge. Ses quatre couches, pour mémoire :
//
// LES QUATRE COUCHES DU NOIR, de la plus faible à la plus forte :
//   grille de points   3%   un mur de LED éteint vu de près - le pas de 24 px
//                           donne une SURFACE au noir sans le remplir
//   bandeau typo      ~5%   le titre en très grand, en tube néon éteint
//   mur LED          0-22%  éteint, traversé d'une vague sur la mesure
//   lueurs radiales   15%   elles marquent la place des pictogrammes, même
//                           quand il n'y a pas de pictogramme
//
// C'EST LE PREMIER ÉTAT DE CE VISUALISEUR QUI BLOOME. La version précédente
// tenait sa luminance sous 0.70 pour rester sous le seuil de la chaîne (0.78)
// - un aplat qui brille n'est plus un aplat. Ici c'est l'inverse : les
// pictogrammes sont tenus à 0.94 pour PASSER le seuil franchement, et c'est ce
// débordement qui fait la fête. Tout le reste est plafonné dessous, séparément
// (voir CRETE_* ci-dessous) : une lueur de fond qui bloomerait remplirait
// l'écran d'un voile et le noir ne serait plus noir.

const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'poster',
  family: 'graphic',
  danceability: 0.70,
  energyLevel: 0.78,
  bpmMin: 100,
  bpmMax: 175,
  movement: 0.60,
  tonality: 0.60,
  prefers: { gridStrength: [0.55, 1.0] },
  requiresTier: 0,
  enabled: true
};

// --- les plafonds de luminance ---------------------------------------------
// Le seuil de bloom de la chaîne partagée vaut 0.78. Trois plafonds, et c'est
// leur SÉPARATION qui fait tenir le critère « les pictogrammes bloomen, les
// lueurs de fond non » : ils vivent dans deux shaders différents et sont
// plafonnés chacun chez soi, jamais ensemble à la fin.
//
// ET LES DEUX PLAFONDS NE SE MESURENT PAS DE LA MÊME FAÇON, c'est le point qui
// a coûté le plus cher ici. La passe de bloom seuille la LUMINANCE Rec.709
// (0.2125 R + 0.7154 G + 0.0721 B), pas le canal maximal. Un rouge saturé dont
// le canal fort vaut 0.94 a une luminance de 0.20 : plafonner au canal maximal
// ne fait donc PAS bloomer, et pire, sur un coeur presque blanc le plafond au
// canal maximal écrase les trois canaux ensemble et FAIT TOMBER la luminance.
// Mesuré : le coeur culminait à 0.54 de luminance, très en dessous du seuil, et
// l'A/B force-du-bloom 0 contre 0.7 ne donnait aucune différence, nulle part.
//   -> ce qui DOIT bloomer est plafonné en LUMINANCE (la grandeur seuillée)
//   -> ce qui NE DOIT PAS bloomer est plafonné au CANAL MAXIMAL, qui majore la
//      luminance : max <= 0.40 implique luminance <= 0.40, quelle que soit la
//      teinte. C'est le plafond sûr, et c'est pour ça qu'il est du bon côté ici.
const CRETE_PICTO = 0.94;   // LUMINANCE. Au-dessus du seuil : ils débordent.
// Les plafonds du fond (mur, lueurs, bandeau) sont dans poster-backdrop.js.

// La zone de sécurité remappée et la géométrie des deux emplacements sont
// dans poster-backdrop.js (ZONE_CHUNK, emplacementsX).
const GRILLE_COLS = 6;
const GRILLE_LIGNES = 4;

// --- le rythme -------------------------------------------------------------
const PUNCH_MS = 80;
const PUNCH_MIN = 1.20;
const PUNCH_MAX = 1.45;
const MOTIFS = ['alterne', 'ensemble', 'deuxdeux', 'rafale'];
// Sans grille de tempo : un temps toutes les 500 ms, une mesure toutes les 2 s.
const TEMPS_LIBRE_S = 0.5;
const MESURE_LIBRE_S = 2.0;

// --- l'amorçage ------------------------------------------------------------
// Un néon ne s'allume pas, il S'AMORCE : la décharge hésite, claque deux fois,
// et seulement ensuite s'établit. C'est la version COURTE de ce que fait
// ravesign.js (qui tire 2 à 4 impulsions de 30 à 60 ms) : ici l'apparition doit
// tenir dans un temps, donc deux impulsions de 25 ms et rien de plus.
// Entre les impulsions le tube n'est pas tout à fait noir - 4% - sans quoi ce
// n'est plus un amorçage mais un clignotement.
const AMORCE = [
  [0.000, 0.025, 0.34],
  [0.050, 0.075, 0.70]
];
const AMORCE_FIN = 0.100;   // au-delà, allumage stable
const AMORCE_ENTRE = 0.04;
// L'extinction, elle, est instantanée : un néon qu'on coupe est noir tout de
// suite. Il n'y a pas d'amorçage à la descente.

// --- le tube ---------------------------------------------------------------
// Le profil de ravesign.js, appliqué à un pictogramme. Trois couches tirées du
// MÊME champ de distance, à trois largeurs :
//   halo       20 px  la lumière diffusée dans l'air autour du verre
//   tube        8 px  la colonne de gaz, dans sa couleur
//   coeur       3 px  le centre chaud, presque blanc, ce que le bloom attrape
const HALO_PX = 20.0;
const TUBE_PX = 8.0;
const COEUR_PX = 3.0;
// LE PICTOGRAMME EST UN TRACÉ, PAS UNE SURFACE. Les SVG Lucide ont
// fill="none" et stroke-width 2 sur une grille de 24 ; l'atlas rastérise le
// TRAIT, et le champ mesure la distance au bord de ce trait. Pour en faire un
// tube d'une largeur à NOUS - et non de celle qu'avait le trait d'origine, qui
// grossit avec le pictogramme - il faut la distance à la LIGNE MÉDIANE du
// trait, soit `d + demi-trait`. Le demi-trait est un invariant de l'atlas :
// 2/24 de la grille rastérisée sur 512 puis réduite à 128 donne
// 2/24 * 128 / 2 = 5.333 pixels de case.
const DEMI_TRAIT_CASE = 5.3333;
// Le champ sature à 12 px de case (SPREAD dans tools/build-icons.mjs) : le
// halo ne peut pas être plus large que ce que le champ sait représenter, sinon
// il s'arrête net sur un anneau dur à la distance de saturation. Sur les
// dalles du Pi (400 px de haut) un pictogramme fait 88 px et la portée
// utilisable tombe à 12 px d'écran : le halo se resserre au lieu de se couper.
const PORTEE_CASE = 12.0;
// Le quad est agrandi pour contenir le halo ; l'UV est remappée pour que la
// case reste au centre.
const MARGE_QUAD = 1.30;

// Les niveaux de l'habillage du noir sont dans poster-backdrop.js.
const ECHO_SUR = 6;              // une apparition sur six part en anneaux
const ECHO_TEMPS = 2;
// L'écart entre deux copies concentriques, en fraction de la taille. Un écart
// de taille, pas une distance : voir le fragment des pictogrammes.
const ECHO_ECART = 0.20;

const TIERS = {
  2: Object.assign({ echo: true }, BACKDROP_TIERS[2]),
  1: Object.assign({ echo: true }, BACKDROP_TIERS[1]),
  0: Object.assign({ echo: false }, BACKDROP_TIERS[0])
};
const TIER_DEFAUT = 2;

const DEG = Math.PI / 180;

function urlParam(nom) {
  if (typeof window === 'undefined' || !window.location) return null;
  return new URLSearchParams(window.location.search).get(nom);
}
function clamp01(v) { return v < 0 ? 0 : (v > 1 ? 1 : v); }

// ---------------------------------------------------------------------------
// Les pictogrammes : un InstancedMesh, un champ de distance, un tube néon
// ---------------------------------------------------------------------------
const pictoVertexShader = `
  precision mediump float;

  attribute vec2 iPos;        // centre, en unités de hauteur
  attribute float iTaille;    // demi-côté de la CASE, unités de hauteur
  attribute float iRot;       // radians, par crans de 90 degrés
  attribute float iMiroir;    // -1 ou 1
  attribute vec2 iCase;       // colonne / ligne dans l'atlas
  attribute float iNiveau;    // 0 = éteint, sinon l'intensité d'amorçage
  attribute float iEcho;      // 0 normal, sinon le rang de l'anneau
  attribute float iTeinte;    // 0 = teinte A, 1 = teinte B

  uniform float uAspect;
  uniform vec2 uResolution;

  varying vec2 vUv;
  varying vec2 vCase;
  varying float vNiveau;
  varying float vEcho;
  varying float vTeinte;
  varying float vPxParSdf;
  varying float vDemiTrait;   // demi-largeur du trait Lucide, en pixels d'écran
  varying float vHaloPx;      // le halo, borné par ce que le champ sait porter

  void main() {
    if (iNiveau < 0.004) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }

    vec2 p = position.xy;
    p.x *= iMiroir;
    float c = cos(iRot), s = sin(iRot);
    vec2 r = vec2(p.x * c - p.y * s, p.x * s + p.y * c);

    // Le quad déborde la case de MARGE_QUAD pour laisser la place au halo ;
    // l'UV est divisée d'autant pour que la case reste centrée dedans.
    vec2 monde = iPos + r * (iTaille * ${MARGE_QUAD.toFixed(3)});
    gl_Position = vec4(monde.x / uAspect, monde.y, 0.0, 1.0);

    vUv = position.xy * (0.5 * ${MARGE_QUAD.toFixed(3)}) + 0.5;
    vCase = iCase;
    vNiveau = iNiveau;
    vEcho = iEcho;
    vTeinte = iTeinte;

    // De quoi convertir la valeur stockée en PIXELS D'ÉCRAN. La case fait
    // 128 px pour une portée de 12, et la case couvre iTaille * hauteur
    // d'écran (le facteur de marge s'annule : il agrandit le quad ET divise
    // l'UV, la case garde donc sa taille).
    float cotePx = iTaille * uResolution.y;
    float caseVersPx = cotePx / 128.0;
    vPxParSdf = (12.0 / 127.0) * caseVersPx * 255.0;
    vDemiTrait = ${DEMI_TRAIT_CASE.toFixed(4)} * caseVersPx;
    // Le champ sature à PORTEE_CASE px de case ; au-delà du bord du trait il
    // ne sait plus rien. La distance maximale HONNÊTE à la ligne médiane vaut
    // donc portée + demi-trait, et le halo ne va pas plus loin - sinon il
    // s'arrête sur un anneau dur à la distance de saturation.
    vHaloPx = min(${HALO_PX.toFixed(1)},
                  (${PORTEE_CASE.toFixed(1)} + ${DEMI_TRAIT_CASE.toFixed(4)}) * caseVersPx * 0.95);
  }
`;

function pictoFragmentShader() {
  return `
  precision mediump float;

  uniform sampler2D uAtlas;
  uniform float uColonnes;
  uniform vec3 uTubeA;
  uniform vec3 uTubeB;
  // uResolution et uAspect sont déclarés par ZONE_CHUNK : les redéclarer ici
  // fait échouer la compilation du fragment, et le programme est abandonné en
  // silence - la couche ne dessine rien et les autres vont bien.

  varying vec2 vUv;
  varying vec2 vCase;
  varying float vNiveau;
  varying float vEcho;
  varying float vTeinte;
  varying float vPxParSdf;
  varying float vDemiTrait;
  varying float vHaloPx;

  ${ZONE_CHUNK}

  // Échantillonne la case, en s'interdisant d'en sortir : les 131 cases sont
  // jointives et le filtrage linéaire irait chercher la voisine sur le bord.
  // Le quad déborde la case, donc cette borne travaille à chaque image.
  float champ(vec2 uv) {
    vec2 borne = clamp(uv, vec2(0.004), vec2(0.996));
    vec2 a = (vCase + borne) / uColonnes;
    return texture2D(uAtlas, a).r;
  }

  void main() {
    // Distance au BORD du trait, en pixels d'écran. Négatif dedans.
    float d = (0.5 - champ(vUv)) * vPxParSdf;
    // Distance à la LIGNE MÉDIANE du trait : c'est elle qui porte le tube. Sans
    // ce recentrage le tube ferait la largeur du trait Lucide, qui grossit avec
    // le pictogramme - un néon a la largeur de son verre, pas celle du dessin.
    float dc = abs(d + vDemiTrait);

    // LES ANNEAUX D'ÉCHO sont des copies AGRANDIES du pictogramme, pas le même
    // champ décalé vers l'extérieur. Décalé, ça ne marche pas : le champ sature
    // à 12 px de case, soit 21 px d'écran de la ligne médiane à la taille
    // courante, donc un anneau demandé à 38 ou 58 px tombait hors de portée et
    // ne se dessinait tout simplement pas. Mesuré : sur les trois anneaux
    // annoncés, un seul existait, et il ajoutait +3 de luminance. Chaque copie
    // échantillonne maintenant SA case à SA propre échelle, ce qui remet la
    // distance dans la portée du champ - et c'est aussi la lecture littérale du
    // brief, « trois copies concentriques ».
    float demiTube = ${(TUBE_PX * 0.5).toFixed(2)};
    float demiCoeur = ${(COEUR_PX * 0.5).toFixed(2)};
    float halo = vHaloPx;
    if (vEcho > 0.5) {
      // « en tube fin DÉCROISSANT » : 0.40, 0.30 puis 0.20 du tube normal, soit
      // 1.6, 1.2 et 0.8 px de demi-largeur. Une épaisseur constante donnait
      // trois anneaux de même luminance moyenne (39.4, 38.3, 37.4) : concentriques
      // mais pas décroissants, ce qui se lit comme une cible, pas comme un écho.
      demiTube *= 0.50 - vEcho * 0.10;
      demiCoeur = 0.0;         // pas de coeur : un écho ne bloome pas
      halo *= 0.45;
    }

    // LES TROIS COUCHES, du même champ, à trois largeurs. Elles s'AJOUTENT :
    // c'est de la lumière, pas des calques de peinture.
    //   halo   large et sans bord du tout - c'est l'air qui brille
    //   tube   la colonne de gaz, dans sa couleur
    //   coeur  le centre presque blanc, ce que le bloom attrape
    float aHalo = pow(max(0.0, 1.0 - dc / halo), 2.6);
    float aTube = 1.0 - smoothstep(demiTube - 1.0, demiTube + 1.0, dc);
    float aCoeur = demiCoeur > 0.0
      ? 1.0 - smoothstep(demiCoeur - 0.8, demiCoeur + 0.8, dc)
      : 0.0;

    vec3 teinte = vTeinte < 0.5 ? uTubeA : uTubeB;
    vec3 col = teinte * (aHalo * 0.30 + aTube * 0.85)
             + mix(teinte, vec3(1.0), 0.80) * aCoeur * 0.95;
    col *= vNiveau;

    // L'INTÉRIEUR DE LA FORME RESTE NOIR. Rien n'est rempli : le pictogramme
    // est un tracé, le tube EST le dessin, et on voit le fond au travers. C'est
    // la différence exacte avec la version précédente, où le pictogramme était
    // une surface découpée dans un aplat.

    col *= zoneSecurite();
    // EN LUMINANCE, pas au canal maximal : c'est la luminance que la passe de
    // bloom seuille, et un plafond au canal maximal écraserait le coeur
    // presque blanc jusqu'à le faire passer SOUS le seuil.
    float lum = dot(col, ${LUMA});
    if (lum > ${CRETE_PICTO.toFixed(2)}) col *= ${CRETE_PICTO.toFixed(2)} / lum;

    if (max(col.r, max(col.g, col.b)) < 0.0015) discard;
    // AdditiveBlending vaut (SRC_ALPHA, ONE) : la contribution réelle est
    // rgb * alpha. Porter l'intensité dans les deux la mettrait au carré et
    // éteindrait tout le halo. Elle vit dans col ; alpha reste à 1.
    gl_FragColor = vec4(col, 1.0);
  }
`;
}

export class PosterVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : TIER_DEFAUT;
    this._tier = TIERS[tier] ? tier : TIER_DEFAUT;
    this._opts = TIERS[this._tier];

    // ?postericons=sun,moon,heart impose le vivier.
    const forces = urlParam('postericons');
    this._vivierForce = forces ? forces.split(',').map((s) => s.trim()).filter(Boolean) : null;
    // ?posterpool=1 imprime le vivier avec la source et le poids de chacun.
    this._tracePool = urlParam('posterpool') === '1';
    // ?posterpattern=alterne|ensemble|deuxdeux|rafale
    const mot = urlParam('posterpattern');
    this._motifForce = MOTIFS.indexOf(mot) >= 0 ? MOTIFS.indexOf(mot) : null;
    // ?postergrid=1 déclenche la grille du drop en boucle.
    this._grilleForce = urlParam('postergrid') === '1';
    // ?posteratlas=1 montre un pictogramme seul, plein cadre.
    this._voirAtlas = urlParam('posteratlas') === '1';
    // ?posterwall=1 allume le mur LED en permanence.
    this._murForce = urlParam('posterwall') === '1';
    // ?posterstrike=1 ralentit l'amorçage d'un facteur 8, pour le voir.
    this._amorceLente = urlParam('posterstrike') === '1' ? 8 : 1;
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    scene.fog = null;

    const atlas = getIconAtlas();
    this._atlas = atlas;

    // Le fond porte les uniformes communs (zone de sécurité, résolution,
    // teinte des tubes) ; les pictogrammes y ajoutent les leurs, dans le MÊME
    // objet, comme avant l'extraction.
    this._backdrop = new PosterBackdrop(Object.assign({ random: () => this._hasard(), renderOrder: 0 }, this._opts));
    this._backdrop.init(scene);
    this._uniforms = this._backdrop.uniforms;
    Object.assign(this._uniforms, {
      uAtlas: { value: atlas ? atlas.texture : null },
      uColonnes: { value: atlas ? atlas.manifeste.colonnes : 16 }
    });

    this._construirePictos();

    // --- état ---
    this._temps = 0;
    this._vivier = [];
    this._pisteVue = undefined;
    this._motif = 0;
    this._tempsIndex = 0;   // le rang du temps dans la mesure, pour le motif
    this._dernierBeat = null;
    this._derniereBarre = null;
    this._dernierePhrase = null;
    this._dernierMode = null;
    this._grilleJusqu = -1;
    this._preDrop = 0;
    this._dernierLibre = 0;
    this._dernierLibreBarre = 0;
    this._basse = 0.5;
    this._aigu = 0.5;
    this._dernierNom = ['', ''];
    this._emplacements = [
      { nom: null, naissance: -9, rot: 0, miroir: 1, taille: 0.22, echo: 0, vivant: false },
      { nom: null, naissance: -9, rot: 0, miroir: 1, taille: 0.22, echo: 0, vivant: false }
    ];
    this._backdrop.setPalette(null);
    this._graine = 1;
  }

  // Un générateur pseudo-aléatoire À NOUS. Math.random marcherait, mais le
  // même morceau donnerait une enseigne différente à chaque lecture, et ce
  // visualiseur a une identité par morceau : le vivier est déterministe, le
  // déroulé aussi.
  _hasard() {
    this._graine = (this._graine * 1103515245 + 12345) % 2147483648;
    return this._graine / 2147483648;
  }

  _construirePictos() {
    const MAX = 32;
    this._max = MAX;
    const g = new THREE.InstancedBufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array([
      -1, -1, 0, 1, -1, 0, 1, 1, 0, -1, 1, 0]), 3));
    g.setIndex([0, 1, 2, 0, 2, 3]);
    this._att = {
      iPos: new Float32Array(MAX * 2), iTaille: new Float32Array(MAX),
      iRot: new Float32Array(MAX), iMiroir: new Float32Array(MAX),
      iCase: new Float32Array(MAX * 2), iNiveau: new Float32Array(MAX),
      iEcho: new Float32Array(MAX), iTeinte: new Float32Array(MAX)
    };
    for (const [nom, arr] of Object.entries(this._att)) {
      const taille = (nom === 'iPos' || nom === 'iCase') ? 2 : 1;
      g.setAttribute(nom, new THREE.InstancedBufferAttribute(arr, taille));
    }
    g.instanceCount = MAX;
    this._pictoGeo = g;
    this._pictoMat = new THREE.ShaderMaterial({
      uniforms: this._uniforms,
      vertexShader: pictoVertexShader,
      fragmentShader: pictoFragmentShader(),
      transparent: true, depthTest: false, depthWrite: false,
      blending: THREE.AdditiveBlending
    });
    this._pictos = new THREE.Mesh(g, this._pictoMat);
    this._pictos.frustumCulled = false;
    this._pictos.renderOrder = 1;
    this._scene.add(this._pictos);
  }

  // --- les pictogrammes -----------------------------------------------------
  _caseDe(nom) {
    const c = this._atlas && this._atlas.manifeste.cases[nom];
    return c ? [c.col, c.ligne] : null;
  }

  _poser(cote) {
    const liste = actives(this._vivier, this._progress);
    if (!liste.length) return;
    const nom = tirer(liste, () => this._hasard(), this._dernierNom[cote]);
    this._dernierNom[cote] = nom;
    const e = this._emplacements[cote];
    e.nom = nom;
    e.naissance = this._temps;
    e.vivant = true;
    e.rot = Math.floor(this._hasard() * 4) * 90 * DEG;       // par crans de 90
    e.miroir = this._hasard() < 1 / 3 ? -1 : 1;              // une fois sur trois
    e.taille = 0.22 * (0.8 + this._hasard() * 0.4);          // +/- 20%
    e.echo = (this._opts.echo && this._hasard() < (1 / ECHO_SUR) * (0.5 + this._aigu)) ? 1 : 0;
  }

  _eteindre(cote) { this._emplacements[cote].vivant = false; }

  // L'AMORÇAGE. Rend l'intensité du tube en fonction de l'âge de l'apparition :
  // deux impulsions de 25 ms, un quasi-noir entre elles, puis l'allumage stable.
  _amorcage(age) {
    const a = age / this._amorceLente;
    if (a >= AMORCE_FIN) return 1;
    for (const p of AMORCE) if (a >= p[0] && a < p[1]) return p[2];
    return AMORCE_ENTRE;
  }

  _ecrireInstances() {
    const a = this._att;
    for (let i = 0; i < this._max; i++) a.iNiveau[i] = 0;

    // R19 : les emplacements sont à hauteur du centre de scène, pas de la pochette
    const sc = safeZoneUniforms.uStageCenter.value;
    const sh = safeZoneUniforms.uSafeHalfSize.value;
    const [xG, xD] = this._backdrop.emplacementsX();

    // Les lueurs marquent la place des emplacements, pictogramme ou pas.
    this._uniforms.uPosA.value.set(xG, sc.y);
    this._uniforms.uPosB.value.set(xD, sc.y);

    const grilleActive = this._temps < this._grilleJusqu || this._grilleForce;

    if (!grilleActive) {
      for (let cote = 0; cote < 2; cote++) {
        const e = this._emplacements[cote];
        if (!e.vivant || !e.nom) continue;
        const c = this._caseDe(e.nom);
        if (!c) continue;
        // LE PUNCH : 130% (jusqu'à 145% quand les basses poussent) qui revient
        // à 100% en 80 ms. Linéaire, pas amorti : une enseigne ne rebondit pas.
        const age = this._temps - e.naissance;
        const ms = age * 1000;
        const amp = PUNCH_MIN + (PUNCH_MAX - PUNCH_MIN) * clamp01(this._basse);
        const punch = ms >= PUNCH_MS ? 1 : amp + (1 - amp) * (ms / PUNCH_MS);
        const niveau = this._amorcage(age);
        const x = cote === 0 ? xG : xD;
        const base = cote;
        a.iPos[base * 2] = x; a.iPos[base * 2 + 1] = sc.y;
        a.iTaille[base] = e.taille * punch;
        a.iRot[base] = e.rot; a.iMiroir[base] = e.miroir;
        a.iCase[base * 2] = c[0]; a.iCase[base * 2 + 1] = c[1];
        a.iNiveau[base] = niveau; a.iEcho[base] = 0; a.iTeinte[base] = cote;

        if (e.echo) {
          // Trois copies concentriques en tube fin décroissant, qui s'éteignent
          // en deux temps. Sur du noir, un anneau de lumière qui s'efface est
          // beaucoup plus lisible qu'un contour d'encre qui disparaît.
          const vie = age / Math.max(0.2, ECHO_TEMPS * this._periodeTemps());
          for (let k = 0; k < 3; k++) {
            if (vie > (k + 1) / 3) continue;
            const idx = 2 + cote * 3 + k;
            a.iPos[idx * 2] = x; a.iPos[idx * 2 + 1] = sc.y;
            a.iTaille[idx] = e.taille * punch * (1 + (k + 1) * ECHO_ECART);
            a.iRot[idx] = e.rot; a.iMiroir[idx] = e.miroir;
            a.iCase[idx * 2] = c[0]; a.iCase[idx * 2 + 1] = c[1];
            // L'anneau s'éteint DANS son rang : plus il est loin, plus il est
            // faible, et il s'en va quand son rang expire.
            a.iNiveau[idx] = niveau * (1 - clamp01(vie * 3 - k)) * 0.8;
            a.iEcho[idx] = k + 1; a.iTeinte[idx] = cote;
          }
        }
      }
    } else {
      // LA GRILLE DU DROP : le pictogramme le mieux fondé du vivier, 6 x 4 sur
      // tout le cadre, en néon, sur le mur LED allumé.
      const nom = this._vivier.length ? this._vivier[0].nom : null;
      const c = nom && this._caseDe(nom);
      if (c) {
        const asp = safeZoneUniforms.uAspect.value || 1.78;
        for (let l = 0; l < GRILLE_LIGNES; l++) {
          for (let co = 0; co < GRILLE_COLS; co++) {
            const idx = 8 + l * GRILLE_COLS + co;
            a.iPos[idx * 2] = (co + 0.5) / GRILLE_COLS * 2 * asp - asp;
            a.iPos[idx * 2 + 1] = 1 - (l + 0.5) / GRILLE_LIGNES * 2;
            a.iTaille[idx] = Math.min(asp / GRILLE_COLS, 1 / GRILLE_LIGNES) * 0.70;
            a.iRot[idx] = 0; a.iMiroir[idx] = 1;
            a.iCase[idx * 2] = c[0]; a.iCase[idx * 2 + 1] = c[1];
            a.iNiveau[idx] = 1; a.iEcho[idx] = 0; a.iTeinte[idx] = 0;
          }
        }
      }
    }

    for (const nom of Object.keys(this._att)) {
      this._pictoGeo.getAttribute(nom).needsUpdate = true;
    }
  }

  _periodeTemps() {
    return this._bpm > 0 ? 60 / this._bpm : TEMPS_LIBRE_S;
  }

  // Le motif décide qui apparaît sur quel temps. C'est la seule règle de
  // rythme des pictogrammes ; tout le reste est du tirage.
  _surLeTemps() {
    const t = this._tempsIndex % 4;
    const motif = this._motifForce != null ? this._motifForce : this._motif;
    if (motif === 0) {                     // ALTERNE
      if (t % 2 === 0) { this._poser(0); this._eteindre(1); }
      else { this._poser(1); this._eteindre(0); }
    } else if (motif === 1) {              // ENSEMBLE
      if (t === 0) { this._poser(0); this._poser(1); }
      else { this._eteindre(0); this._eteindre(1); }
    } else if (motif === 2) {              // DEUX-DEUX
      if (t === 0) { this._poser(0); this._eteindre(1); }
      else if (t === 2) { this._poser(1); this._eteindre(0); }
    } else {                               // RAFALE
      this._poser(0); this._poser(1);
    }
    // La pulsation des lueurs et le caractère du bandeau qui s'allume : le fond.
    this._backdrop.onBeat();
    this._tempsIndex++;
  }

  // La teinte, la vague et le cran du bandeau : le fond.
  _surLaMesure() {
    this._backdrop.onBar();
  }

  _surLaPhrase() {
    if (this._motifForce == null) {
      this._motif = (this._motif + 1 + Math.floor(this._hasard() * 3)) % MOTIFS.length;
    }
    // Une phrase sur quatre bicolore : le fond tient la couleur.
    this._backdrop.onPhrase();
  }

  update(deltaTime, profile) {
    // PAUSE : gel complet, les tubes allumés restent allumés. Une enseigne ne
    // s'éteint pas parce qu'on a appuyé sur pause.
    if (profile.playbackState !== 'PLAYING') return;

    this._temps += deltaTime;
    const director = profile.director;
    this._bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    this._progress = profile.duration > 0 ? clamp01(profile.progress) : 0.5;

    const bandes = (director && director.hasReliableGrid && director.bands) ? director.bands : null;
    if (bandes) {
      this._basse += ((bandes.low != null ? bandes.low : 0.5) - this._basse) * Math.min(1, deltaTime / 0.25);
      this._aigu += ((bandes.high != null ? bandes.high : 0.5) - this._aigu) * Math.min(1, deltaTime / 0.35);
    }

    // --- le vivier, au changement de morceau ---
    const piste = (profile.title || '') + '\u0000' + (profile.artist || '') + '\u0000' + (profile.trackId || '');
    if (piste !== this._pisteVue) {
      this._pisteVue = piste;
      this._graine = 1;
      if (this._vivierForce) {
        this._vivier = this._vivierForce.map((nom, i) => ({ nom, poids: 1 - i * 0.05, source: 'force' }));
      } else {
        this._vivier = construireVivier({
          title: profile.title, artist: profile.artist, album: profile.album,
          genre: profile.trackFeatures && profile.trackFeatures.genre,
          character: director && director.character,
          palette: profile.artworkPalette
        });
      }
      if (this._tracePool) {
        console.log('[poster] vivier pour « ' + (profile.title || '(sans titre)') + ' »');
        for (const e of this._vivier) {
          console.log('   ' + e.nom.padEnd(14) + ' ' + e.source.padEnd(10) + ' poids ' + e.poids.toFixed(3));
        }
      }
      this._backdrop.setTitle(cleanDisplayTitle(profile.title) || cleanDisplayArtist(profile.artist)
        || profile.album || null);
      this._dernierNom = ['', ''];
    }

    if (profile.artworkPalette !== this._paletteVue) {
      this._paletteVue = profile.artworkPalette;
      this._backdrop.setPalette(profile.artworkPalette);
    }

    // --- les trois horloges ---
    const beat = director && director.beat != null ? director.beat : profile.beatPhase;
    const barre = director && director.bar != null ? director.bar : profile.barPhase;
    const phrase = director && director.phrase != null ? director.phrase : profile.phrasePhase;

    if (beat != null) {
      if (this._dernierBeat != null && beat < this._dernierBeat) this._surLeTemps();
      this._dernierBeat = beat;
    } else if (this._temps - this._dernierLibre >= TEMPS_LIBRE_S) {
      // SANS BPM : ALTERNE toutes les 500 ms, mesure toutes les 2 s.
      this._dernierLibre = this._temps;
      this._motif = this._motifForce != null ? this._motifForce : 0;
      this._surLeTemps();
    }
    if (barre != null) {
      if (this._derniereBarre != null && barre < this._derniereBarre) this._surLaMesure();
      this._derniereBarre = barre;
    } else if (this._temps - this._dernierLibreBarre >= MESURE_LIBRE_S) {
      this._dernierLibreBarre = this._temps;
      this._surLaMesure();
    }
    if (phrase != null) {
      if (this._dernierePhrase != null && phrase < this._dernierePhrase) this._surLaPhrase();
      this._dernierePhrase = phrase;
    }

    const periode = Math.max(0.12, this._periodeTemps());

    // --- le drop ---
    const mode = director ? director.mode : null;
    const evt = director && director.nextEvent;
    if (evt && evt.type === 'drop' && evt.msUntil != null) {
      // Quatre temps avant : les pictogrammes accélèrent jusqu'à un par
      // demi-temps. On ne fait que resserrer l'horloge.
      const temps = evt.msUntil / 1000 / periode;
      this._preDrop = temps > 0 && temps < 4 ? 1 - temps / 4 : 0;
    } else {
      this._preDrop = 0;
    }
    if (this._preDrop > 0 && beat != null) {
      const demi = (beat + 0.5) % 1;
      if (this._demiPrec != null && demi < this._demiPrec && this._hasard() < this._preDrop) this._surLeTemps();
      this._demiPrec = demi;
    }
    if (mode === 'drop' && this._dernierMode !== 'drop') {
      this._grilleJusqu = this._temps + 4 * periode;   // une mesure
    }
    this._dernierMode = mode;
    const grilleActive = this._temps < this._grilleJusqu || this._grilleForce;
    // Le fond : la vague, le mur du drop, la couleur qui suit, les
    // pulsations, le bandeau, la résolution et la place des lueurs.
    this._backdrop.update(deltaTime, periode, grilleActive || this._murForce);

    this._ecrireInstances();

    if (this._voirAtlas && this._atlas) {
      // ?posteratlas=1 : un seul pictogramme, plein cadre, allumé en continu.
      const a = this._att;
      for (let i = 0; i < this._max; i++) a.iNiveau[i] = 0;
      a.iPos[0] = 0; a.iPos[1] = 0; a.iTaille[0] = 0.8; a.iRot[0] = 0; a.iMiroir[0] = 1;
      const c = this._caseDe(this._vivier.length ? this._vivier[0].nom : 'heart') || [0, 0];
      a.iCase[0] = c[0]; a.iCase[1] = c[1];
      a.iNiveau[0] = 1; a.iEcho[0] = 0; a.iTeinte[0] = 0;
      for (const nom of Object.keys(this._att)) this._pictoGeo.getAttribute(nom).needsUpdate = true;
    }
  }

  dispose() {
    this._scene.remove(this._pictos);
    this._pictoGeo.dispose();
    this._pictoMat.dispose();
    this._backdrop.dispose();
  }
}
